// Searchable, keyboard-navigable Degree autocomplete grouped by level.
// Backed by the shared degree store so any admin change to the master list
// is reflected instantly across every consumer.
//
// Value shape (matches existing "select"/"degree" handling elsewhere):
//   { value: "<Name (Abbr)>" | "Others" | "", other: "" }
// When value === "Others" the parent renders a free-text input.

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import {
    useDegrees,
    groupByLevel,
    degreeLabel,
    searchDegrees,
} from "@/lib/degreeStore";

const OTHERS_VALUE = "Others";

// Split a string into <mark>…</mark> segments around matched substrings.
function highlight(text, query) {
    const q = (query || "").trim();
    if (!q) return text;
    try {
        const re = new RegExp(
            `(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
            "ig",
        );
        const parts = text.split(re);
        return parts.map((p, i) =>
            re.test(p) ? (
                <mark
                    key={i}
                    className="bg-[#EAE5D9] text-[#0F1F38] px-0.5"
                >
                    {p}
                </mark>
            ) : (
                <span key={i}>{p}</span>
            ),
        );
    } catch {
        return text;
    }
}

export default function DegreeSelect({
    value,
    onChange,
    placeholder = "Select your degree",
    testid,
    surface = "beige", // "beige" (candidate form) | "white" (admin edit)
}) {
    const { degrees, loading } = useDegrees();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);

    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selectedLabel = value?.value || "";
    const isOthers = selectedLabel === OTHERS_VALUE;

    // Flat, filtered, ranked list — plus grouping for display when the
    // query is empty. When query is non-empty we drop group headers and
    // show a single ranked list (best-match first).
    const filtered = useMemo(
        () => searchDegrees(degrees, query),
        [degrees, query],
    );

    const grouped = useMemo(
        () => (query.trim() ? null : groupByLevel(filtered)),
        [filtered, query],
    );

    // Flat item list used for keyboard navigation. Includes the trailing
    // "Others" row so users can arrow-into it.
    const flatItems = useMemo(() => {
        const rows = [];
        if (grouped) {
            grouped.forEach((g) => {
                if (g.items.length) {
                    rows.push({ kind: "header", label: g.level });
                    g.items.forEach((d) =>
                        rows.push({ kind: "item", degree: d }),
                    );
                }
            });
        } else {
            filtered.forEach((d) => rows.push({ kind: "item", degree: d }));
        }
        rows.push({ kind: "others" });
        return rows;
    }, [grouped, filtered]);

    // Reset the active index whenever the visible list changes.
    useEffect(() => {
        const firstSelectable = flatItems.findIndex(
            (r) => r.kind !== "header",
        );
        setActiveIdx(firstSelectable === -1 ? 0 : firstSelectable);
    }, [flatItems]);

    // Focus the search input the moment we open.
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 0);
        else setQuery("");
    }, [open]);

    // Click-outside closes the popover.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Keep the active row scrolled into view.
    useEffect(() => {
        if (!open) return;
        const el = listRef.current?.querySelector(
            `[data-idx="${activeIdx}"]`,
        );
        if (el && el.scrollIntoView) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [activeIdx, open]);

    const selectRow = (row) => {
        if (!row) return;
        if (row.kind === "others") {
            onChange({ value: OTHERS_VALUE, other: value?.other || "" });
        } else if (row.kind === "item") {
            onChange({ value: degreeLabel(row.degree), other: "" });
        }
        setOpen(false);
    };

    const moveActive = (delta) => {
        if (!flatItems.length) return;
        let i = activeIdx;
        for (let step = 0; step < flatItems.length; step++) {
            i = (i + delta + flatItems.length) % flatItems.length;
            if (flatItems[i].kind !== "header") {
                setActiveIdx(i);
                return;
            }
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            moveActive(1);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            moveActive(-1);
        } else if (e.key === "Enter") {
            e.preventDefault();
            selectRow(flatItems[activeIdx]);
        } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
        }
    };

    const triggerBg =
        surface === "beige" ? "bg-[#F7F5F0]" : "bg-white";
    const panelBg = "bg-white";
    const border = "border-[#0F1F38]/25";
    const displayText = isOthers
        ? "Others"
        : selectedLabel || placeholder;
    const isPlaceholder = !selectedLabel;

    // Only real degree matches contribute to the "no results" state;
    // "Others" is always available as a fallback row so users can bail
    // out to free-text entry no matter what they typed.
    const hasMatches = filtered.length > 0;

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={testid}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`mt-2 h-11 w-full inline-flex items-center justify-between gap-2 px-3 border ${border} ${triggerBg} rounded-xl text-sm text-left transition-colors hover:border-[#0F1F38]/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-[#0F1F38]/30`}
            >
                <span
                    className={`truncate ${
                        isPlaceholder
                            ? "text-[#0F1F38]/50"
                            : "text-[#0F1F38]"
                    }`}
                >
                    {displayText}
                </span>
                <ChevronDown
                    size={16}
                    className="text-[#0F1F38]/50 flex-shrink-0"
                />
            </button>

            {open && (
                <div
                    data-testid={`${testid}-panel`}
                    className={`absolute z-40 mt-1 w-full ${panelBg} border ${border} shadow-lg rounded-xl overflow-hidden`}
                >
                    <div
                        className={`flex items-center gap-2 px-3 h-11 border-b ${border}`}
                    >
                        <Search
                            size={14}
                            className="text-[#0F1F38]/50 flex-shrink-0"
                        />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Search by name or abbreviation…"
                            data-testid={`${testid}-search`}
                            className="w-full bg-transparent outline-none text-sm placeholder:text-[#0F1F38]/40 text-[#0F1F38]"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={() => setQuery("")}
                                aria-label="Clear search"
                                data-testid={`${testid}-clear`}
                                className="text-[#0F1F38]/40 hover:text-[#0F1F38] transition-colors"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div
                        ref={listRef}
                        role="listbox"
                        className="max-h-72 overflow-y-auto py-1"
                    >
                        {loading && !degrees.length ? (
                            <div className="px-3 py-6 text-center text-xs text-[#0F1F38]/60">
                                Loading degrees…
                            </div>
                        ) : !hasMatches ? (
                            <div
                                data-testid={`${testid}-empty`}
                                className="px-3 py-6 text-center text-xs text-[#0F1F38]/60"
                            >
                                No matching degree found
                            </div>
                        ) : null}

                        {flatItems.map((row, idx) => {
                            if (row.kind === "header") {
                                return (
                                    <div
                                        key={`h-${row.label}`}
                                        data-idx={idx}
                                        className="px-3 pt-3 pb-1 text-[10px] tracking-label uppercase font-bold text-[#0F1F38] bg-[#F7F5F0] border-t border-b border-[#0F1F38]/10"
                                    >
                                        {row.label}
                                    </div>
                                );
                            }
                            if (row.kind === "others") {
                                return (
                                    <button
                                        key="others-row"
                                        type="button"
                                        data-idx={idx}
                                        data-testid={`${testid}-others`}
                                        onMouseEnter={() => setActiveIdx(idx)}
                                        onClick={() => selectRow(row)}
                                        className={`w-full text-left px-4 py-2 text-sm border-t border-[#0F1F38]/10 mt-1 ${
                                            activeIdx === idx
                                                ? "bg-[#0F1F38] text-[#F7F5F0]"
                                                : "text-[#0F1F38] hover:bg-[#F7F5F0]"
                                        }`}
                                    >
                                        Others (type manually)
                                    </button>
                                );
                            }
                            const d = row.degree;
                            const label = degreeLabel(d);
                            const isActive = activeIdx === idx;
                            const isSelected = label === selectedLabel;
                            return (
                                <button
                                    key={d.id}
                                    type="button"
                                    data-idx={idx}
                                    data-testid={`${testid}-opt-${d.abbreviation.replace(/\s+/g, "_")}`}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => selectRow(row)}
                                    className={`w-full text-left px-4 py-2 text-sm ${
                                        isActive
                                            ? "bg-[#0F1F38] text-[#F7F5F0]"
                                            : isSelected
                                              ? "bg-[#EAE5D9] text-[#0F1F38]"
                                              : "text-[#0F1F38] hover:bg-[#F7F5F0]"
                                    }`}
                                >
                                    {highlight(label, query)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export { OTHERS_VALUE };
